/*
 * Hat unlock checks. Run after each minigame ends, after each chat phrase, and
 * periodically while the player is connected (for time-based unlocks).
 *
 * The `UnlockContext` carries every counter any HatUnlock variant might want
 * to read; callers can pass 0 for irrelevant fields.
 */

import { save } from '@/state/save';
import { HATS, type Hat, type HatUnlock } from '@/content/hats';

export interface UnlockContext {
  /** Score of the most recent forage run (0 if not relevant). */
  forageScore: number;
  /** Score of the most recent fish run (0 if not relevant). */
  fishScore: number;
  /** Score of the most recent stargaze run (0 if not relevant). */
  stargazeScore?: number;
}

function meets(unlock: HatUnlock, ctx: UnlockContext): boolean {
  switch (unlock.type) {
    case 'forage':
      return ctx.forageScore >= unlock.value;
    case 'fish':
      return ctx.fishScore >= unlock.value;
    case 'lifetimeForage':
      return save.lifetimeForage >= unlock.value;
    case 'lifetimeFish':
      return save.lifetimeFish >= unlock.value;
    case 'chatCount':
      return save.chatCount >= unlock.value;
    case 'playMinutes':
      return save.playSeconds / 60 >= unlock.value;
    case 'consecutiveSessions':
      return save.consecutiveSessions >= unlock.value;
    case 'stargaze':
      return (ctx.stargazeScore ?? 0) >= unlock.value;
  }
}

/** Returns the list of newly-unlocked hats (and mutates save.unlocks). */
export function checkUnlocks(ctx: UnlockContext): Hat[] {
  const newly: Hat[] = [];
  for (const h of HATS) {
    if (!h.unlock) continue;
    if (save.unlocks.includes(h.name)) continue;
    if (meets(h.unlock, ctx)) {
      save.unlocks.push(h.name);
      newly.push(h);
    }
  }
  return newly;
}
